import {
  type RowModel,
  assignTableAPIs,
  functionalUpdate,
  type RowData,
  type Table_Internal,
  type TableFeature,
  type TableFeatures,
  type Updater,
} from '@tanstack/react-table';

import type {
  DataTablePaginationState,
  RowPaginationFeatureConstructors,
} from './pagination-types.js';
import {
  DATA_TABLE_DEFAULT_PAGE_SIZE_OPTION,
  DATA_TABLE_DEFAULT_PAGINATION_PAGE_INDEX,
} from '../../constants.js';

/**
 * @internal
 */
export function table_getPrePaginatedRowModel<
  TFeatures extends TableFeatures,
  TData extends RowData,
>(table: Table_Internal<TFeatures, TData>): RowModel<TFeatures, TData> {
  return table.getRowOrderRowModel();
}

/**
 * Helper function to update the pagination state with the provided updater function or value.
 * @internal
 */
export function table_setPagination<
  TFeatues extends TableFeatures,
  TData extends RowData,
>(
  table: Table_Internal<TFeatues, TData>,
  updater: Updater<DataTablePaginationState>,
  trigger?: 'user' | 'internal',
) {
  const safeUpdater: Updater<DataTablePaginationState> = (old) => {
    const newState: DataTablePaginationState = functionalUpdate(updater, old);

    return newState;
  };

  return table.options.onPaginationChange?.(safeUpdater, trigger);
}

/**
 * Helper function to update the page index with the provided updater function or value.
 * @internal
 */
export function table_setPageIndex<
  TFeatues extends TableFeatures,
  TData extends RowData,
>(
  table: Table_Internal<TFeatues, TData>,
  updater: Updater<number>,
  trigger?: 'user' | 'internal',
) {
  table_setPagination(
    table,
    (old) => {
      let pageIndex = functionalUpdate(updater, old.pageIndex);

      const maxPageIndex =
        typeof table.options.pageCount === 'undefined' ||
        table.options.pageCount === -1
          ? Number.MAX_SAFE_INTEGER
          : table.options.pageCount - 1;

      pageIndex = Math.max(0, Math.min(pageIndex, maxPageIndex));

      return {
        ...old,
        pageIndex,
      };
    },
    trigger,
  );
}

/**
 * Override of tanstack function in order to call the correct table_setPagination
 */
export function table_setPageSize<
  TFeatures extends TableFeatures,
  TData extends RowData,
>(table: Table_Internal<TFeatures, TData>, updater: Updater<number>) {
  table_setPagination(table, (old) => {
    const pageSize = Math.max(1, functionalUpdate(updater, old.pageSize));
    const topRowIndex = old.pageSize * old.pageIndex;
    const pageIndex = Math.floor(topRowIndex / pageSize);

    return {
      ...old,
      pageIndex,
      pageSize,
    };
  });
}

export function table_resetPageSize<
  TFeatures extends TableFeatures,
  TData extends RowData,
>(table: Table_Internal<TFeatures, TData>, defaultState?: boolean) {
  table_setPageSize(
    table,
    defaultState
      ? DATA_TABLE_DEFAULT_PAGE_SIZE_OPTION
      : table.initialState.pagination?.pageSize ??
          DATA_TABLE_DEFAULT_PAGE_SIZE_OPTION,
  );
}

/**
 * Override of tanstack function in order to call the correct table_setPageIndex
 * @internal
 */
export function table_autoResetPageIndex<
  TFeatures extends TableFeatures,
  TData extends RowData,
>(table: Table_Internal<TFeatures, TData>) {
  if (
    table.options.autoResetAll ??
    table.options.autoResetPageIndex ??
    !table.options.manualPagination
  ) {
    table_resetPageIndex(table);
  }
}
/**
 * Helper function to reset the page index to the default page index.
 * @internal
 */
export function table_resetPageIndex<
  TFeatues extends TableFeatures,
  TData extends RowData,
>(table: Table_Internal<TFeatues, TData>, defaultState?: boolean) {
  table_setPageIndex(
    table,
    defaultState
      ? DATA_TABLE_DEFAULT_PAGINATION_PAGE_INDEX
      : table.initialState?.pagination?.pageIndex ??
          DATA_TABLE_DEFAULT_PAGINATION_PAGE_INDEX,
    'internal',
  );
}

/**
 * Constructs the pagination feature for the table.
 * @internal
 */
export function constructRowPaginationFeature<
  TFeatures extends TableFeatures,
  TData extends RowData,
>(): TableFeature<RowPaginationFeatureConstructors<TFeatures, TData>> {
  return {
    constructTableAPIs: (table) => {
      assignTableAPIs('rowPaginationFeature', table, {
        table_autoResetPageIndex: {
          fn: () => table_autoResetPageIndex(table),
        },
        table_setPagination: {
          fn: (
            updater: Updater<DataTablePaginationState>,
            trigger?: 'user' | 'internal',
          ) => table_setPagination(table, updater, trigger),
        },
        table_setPageIndex: {
          fn: (updater: Updater<number>, trigger?: 'user' | 'internal') =>
            table_setPageIndex(table, updater, trigger),
        },
        table_setPageSize: {
          fn: (updater) => table_setPageSize(table, updater),
        },
        table_resetPageIndex: {
          fn: (defaultState?: boolean) =>
            table_resetPageIndex(table, defaultState),
        },
        table_resetPageSize: {
          fn: (defaultState) => table_resetPageSize(table, defaultState),
        },
        table_getPrePaginatedRowModel: {
          fn: () => table_getPrePaginatedRowModel(table),
        },
      });
    },
  };
}

/**
 * Feature implementation for row pagination.
 * @internal
 */
export const DataTableRowPaginationFeature = constructRowPaginationFeature();
